import PageHeader from "../Components/PageHeader";
import Section from "../Components/Section";
import Card from "../Components/Card";
import { FaRegLightbulb, FaRegHandshake } from "react-icons/fa6";
import { LuSettings } from "react-icons/lu";
import { FaPeopleGroup } from "react-icons/fa6";
import useChangeTitle from "../Hooks/useChangeTitle.jsx";

export default function Plumbing() {
  useChangeTitle("Plumbing Services");

  const plumbingFeatures = [
    {
      index: "1",
      title: "Quality Materials",
      desc: "We use high-quality pipes, fittings, and fixtures that meet international standards for durability and reliability.",
      icon: <FaRegLightbulb />,
    },
    {
      index: "2",
      title: "Expert Installation",
      desc: "Professional installation by certified plumbers with years of experience in commercial and residential projects.",
      icon: <FaRegHandshake />,
    },
    {
      index: "3",
      title: "Water Efficiency",
      desc: "Modern plumbing solutions designed for water conservation and efficient distribution systems.",
      icon: <LuSettings />,
    },
    {
      index: "4",
      title: "Maintenance Support",
      desc: "Comprehensive maintenance services to keep your plumbing systems running smoothly and prevent issues.",
      icon: <FaPeopleGroup />,
    },
  ];

  return (
    <>
      <PageHeader heading="Plumbing Systems" />
      
      {/* Hero Section with Aquatic Theme */}
      <div className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/services/Plumbing-624x443.png" 
            alt="Professional Plumbing" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/90 via-blue-800/80 to-teal-900/90"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto">
          <div className="animate-fade-in">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
              Professional
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                Plumbing Solutions
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-cyan-100 max-w-4xl mx-auto leading-relaxed">
              Engineering water flow perfection with innovative supply and drainage systems 
              designed for reliability, efficiency, and sustainable water management.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-10 py-4 rounded-full font-semibold text-lg transform hover:scale-105 transition-all duration-300 shadow-2xl">
                Get Plumbing Quote
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-blue-800 px-10 py-4 rounded-full font-semibold text-lg transform hover:scale-105 transition-all duration-300">
                Water Efficiency Audit
              </button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent"></div>
      </div>

      {/* Services Overview with Water-Themed Design */}
      <Section 
        title="Our Plumbing Services" 
        desc="Comprehensive water management solutions engineered for efficiency and sustainability"
        className="py-24 bg-gradient-to-br from-white via-cyan-50 to-white relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-32 h-32 bg-cyan-100 rounded-full -translate-x-16 -translate-y-16 opacity-50"></div>
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-blue-200 rounded-full translate-x-24 translate-y-24 opacity-30"></div>
        
        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-12">
          {[
            { 
              title: "Water Supply Systems", 
              desc: "Complete water distribution networks for residential and commercial buildings with proper pressure regulation.",
              icon: "🚰",
              color: "from-cyan-500 to-blue-500"
            },
            { 
              title: "Drainage Systems", 
              desc: "Efficient wastewater and stormwater drainage solutions with proper slope and capacity calculations.",
              icon: "🌊",
              color: "from-blue-500 to-indigo-500"
            },
            { 
              title: "Sanitary Systems", 
              desc: "Complete sanitary plumbing installations including fixtures, pipes, and waste management systems.",
              icon: "🛁",
              color: "from-indigo-500 to-purple-500"
            },
            { 
              title: "Hot Water Systems", 
              desc: "Central and distributed hot water systems with energy-efficient heating solutions.",
              icon: "♨️",
              color: "from-red-500 to-orange-500"
            },
            { 
              title: "Irrigation Systems", 
              desc: "Automated irrigation solutions for landscaping and green areas with water conservation features.",
              icon: "🌱",
              color: "from-green-500 to-teal-500"
            },
            { 
              title: "Fire Protection", 
              desc: "Fire sprinkler systems and hydrant installations meeting safety codes and regulations.",
              icon: "🔥",
              color: "from-red-500 to-pink-500"
            }
          ].map((service, index) => (
            <div key={index} className="group relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-600 rounded-2xl blur opacity-0 group-hover:opacity-25 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 overflow-hidden">
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${service.color}`}></div>
                <div className="p-8">
                  <div className="flex items-center mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-r ${service.color} rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300`}>
                      <span className="text-white text-xl">{service.icon}</span>
                    </div>
                    <h3 className="font-bold text-xl text-gray-800 group-hover:text-cyan-600 transition-colors">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{service.desc}</p>
                </div>
                <div className="absolute bottom-0 right-0 w-20 h-20 bg-cyan-50 rounded-tl-full opacity-50"></div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Features Section */}
      <Section 
        title="Why Choose Our Plumbing Services" 
        className="py-16 bg-gray-50"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {plumbingFeatures.map((feature) => (
            <Card
              key={feature.index}
              index={feature.index}
              icon={feature.icon}
              title={feature.title}
              desc={feature.desc}
            />
          ))}
        </div>
      </Section>

      {/* Project Gallery with Aquatic Design */}
      <Section 
        title="Our Plumbing Projects" 
        desc="Showcasing excellence in water management across diverse applications"
        className="py-24 bg-gradient-to-br from-gray-900 via-cyan-900 to-gray-900 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-2 h-32 bg-cyan-400 opacity-20"></div>
          <div className="absolute top-40 right-20 w-2 h-24 bg-blue-500 opacity-30"></div>
          <div className="absolute bottom-20 left-32 w-2 h-28 bg-teal-300 opacity-25"></div>
        </div>
        
        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
          {[
            {
              image: "/images/home/plumbing home.jpg",
              title: "Commercial Buildings",
              desc: "Complete Plumbing Systems",
              gradient: "from-cyan-500/80 to-blue-600/80"
            },
            {
              image: "/images/services/Plumbing-624x443.png",
              title: "Residential Projects",
              desc: "Water Supply & Drainage",
              gradient: "from-blue-500/80 to-indigo-600/80"
            },
            {
              image: "/images/home/c2.jpg",
              title: "Industrial Facilities",
              desc: "Large Scale Plumbing",
              gradient: "from-teal-500/80 to-cyan-600/80"
            }
          ].map((project, index) => (
            <div key={index} className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src={project.image} 
                  alt={project.title} 
                  className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${project.gradient} opacity-0 group-hover:opacity-90 transition-opacity duration-500`}></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                  <h4 className="text-2xl font-bold mb-2 group-hover:text-cyan-300 transition-colors">
                    {project.title}
                  </h4>
                  <p className="text-gray-200 mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200">
                    {project.desc}
                  </p>
                  <div className="w-16 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-300"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
} 